import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Calendar, Shield, UserX, UserCheck, Trash2, Loader2 } from 'lucide-react';
import { Card } from 'components/ui/card';
import Badge from 'components/ui/badge';
import Avatar from 'components/ui/avatar';
import Modal from 'components/ui/Modal';
import { Button } from 'components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@/components/ui';
import { useToast } from 'components/ui/use-toast';
import * as Organization from 'modules/organization';
import { useAuthContext } from 'modules/auth';
import type { OrgMember, OrgRole } from '@examing/types';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from 'modules/auth/permissions';
const ROLES: {
    value: OrgRole;
    labelKey: string;
    descriptionKey: string;
}[] = [
    { value: 'ORG_ADMIN', labelKey: 'role_admin', descriptionKey: 'role_admin_description' },
    { value: 'AUTHOR', labelKey: 'role_author', descriptionKey: 'role_author_description' },
    { value: 'PROCTOR', labelKey: 'role_proctor', descriptionKey: 'role_proctor_description' },
];
const roleBadgeVariant = (role: string): 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
    switch (role) {
        case 'SUPER_ADMIN': return 'danger';
        case 'ORG_ADMIN': return 'primary';
        case 'AUTHOR': return 'success';
        case 'PROCTOR': return 'warning';
        default: return 'secondary';
    }
};
const statusBadgeVariant = (status?: string): 'success' | 'warning' | 'danger' => {
    switch (status) {
        case 'ACTIVE': return 'success';
        case 'SUSPENDED': return 'danger';
        default: return 'warning';
    }
};
const formatDate = (dateStr?: string) => {
    if (!dateStr)
        return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};
interface UserDetailsProps {
    /** Dialog mode: member id from `?member=` instead of the route param. */
    memberId?: string;
    /** Dialog mode: back returns to the members list in place of navigation. */
    onBack?: () => void;
}
const UserDetails: React.FC<UserDetailsProps> = ({ memberId: memberIdProp, onBack }) => {
    const { t } = useTranslation();
    const { id: routeMemberId } = useParams<{
        id: string;
    }>();
    const memberId = memberIdProp ?? routeMemberId;
    const navigate = useNavigate();
    const { state: authState } = useAuthContext();
    const { toast } = useToast();
    // Permission guard
    useEffect(() => {
        if (authState.isFetched && !authState.permissions.includes(PERMISSIONS.MEMBERS_EDIT))
            navigate('/app');
    }, [authState.isFetched, authState.permissions, navigate]);
    const orgId = authState.organization?.id;
    const [member, setMember] = useState<OrgMember | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedRole, setSelectedRole] = useState<string>('');
    const [roleLoading, setRoleLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [removeModal, setRemoveModal] = useState(false);
    const [removeLoading, setRemoveLoading] = useState(false);
    const fetchMember = useCallback(async () => {
        if (!orgId || !memberId)
            return;
        setLoading(true);
        try {
            const { data: res } = await Organization.Api.GetMember(orgId, memberId) as {
                data: OrgMember;
            };
            setMember(res);
            setSelectedRole(res.role);
        }
        catch {
            toast.error(t("failed_to_load_member_details"));
        }
        finally {
            setLoading(false);
        }
    }, [orgId, memberId, toast, t]);
    useEffect(() => {
        fetchMember();
    }, [fetchMember]);
    const handleRoleChange = async (newRole: string) => {
        if (!orgId || !memberId || newRole === member?.role)
            return;
        setSelectedRole(newRole);
        setRoleLoading(true);
        try {
            await Organization.Api.UpdateMemberRole(orgId, memberId, { role: newRole });
            toast.success(t("role_updated_successfully"));
            fetchMember();
        }
        catch {
            toast.error(t("failed_to_update_role"));
            setSelectedRole(member?.role || '');
        }
        finally {
            setRoleLoading(false);
        }
    };
    const handleStatusToggle = async () => {
        if (!orgId || !memberId || !member)
            return;
        const newStatus = member.user?.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
        setStatusLoading(true);
        try {
            await Organization.Api.UpdateMemberStatus(orgId, memberId, { status: newStatus });
            toast.success(`User ${newStatus === 'SUSPENDED' ? 'suspended' : 'activated'} successfully`);
            fetchMember();
        }
        catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to update status');
        }
        finally {
            setStatusLoading(false);
        }
    };
    const handleRemove = async () => {
        if (!orgId || !memberId)
            return;
        setRemoveLoading(true);
        try {
            await Organization.Api.RemoveMember(orgId, memberId);
            toast.success(t("member_removed_successfully"));
            if (onBack) onBack();
            else navigate('/app/admin/organization/users');
        }
        catch {
            toast.error(t("failed_to_remove_member"));
        }
        finally {
            setRemoveLoading(false);
        }
    };
    if (loading) {
        return (<div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground"/>
      </div>);
    }
    const backButton = onBack ? (
        <Button variant="ghost" size="icon" onClick={onBack} aria-label={t("settings.nav_members")}>
          <ArrowLeft size={18}/>
        </Button>
    ) : (
        <Link to="/app/admin/organization/users">
          <Button variant="ghost" size="icon">
            <ArrowLeft size={18}/>
          </Button>
        </Link>
    );
    if (!member) {
        return (<div className="w-full max-w-3xl space-y-6">
        {backButton}
        <div className="text-center py-16 text-muted-foreground">
          <p className="font-medium">{t("member_not_found")}</p>
        </div>
      </div>);
    }
    return (<div className="w-full max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 sm:gap-4">
        {backButton}
        <div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-2xl font-bold text-foreground tracking-tight sm:text-3xl">{t("member_details")}</motion.h1>
        </div>
      </div>

      {/* Profile Card */}
      <Card>
        <div className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
            <Avatar name={member.user?.name} src={member.user?.avatarUrl} size="xl"/>
            <div className="flex-1 min-w-0">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h2 className="truncate text-2xl font-bold text-foreground">{member.user?.name || 'Unknown'}</h2>
                  <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                    <Mail size={14}/>
                    <span className="break-all">{member.user?.email}</span>
                  </div>
                </div>
                <Badge variant={statusBadgeVariant(member.user?.status)}>
                  {member.user?.status || 'PENDING'}
                </Badge>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground sm:gap-6">
                <div className="flex items-center gap-2">
                  <Calendar size={14}/>
                  <span>{t("joined")} {formatDate(member.joinedAt)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={14}/>
                  <Badge variant={roleBadgeVariant(member.role)} size="sm">
                    {member.role.replace(/_/g, ' ')}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Role Management */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">{t("role_and_permissions")}</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">{t("organization_role")}</label>
              {/* Owners and candidates are not editable here: candidates are a
                  sealed, team-managed role and the org panel is staff-only. */}
              {member.isOwner || member.role === 'CANDIDATE' ? (<Badge variant={roleBadgeVariant(member.role)} size="sm">
                  {member.role.replace(/_/g, ' ')}
                </Badge>) : (<Select value={selectedRole} onValueChange={handleRoleChange} disabled={roleLoading}>
                <SelectTrigger className="w-full sm:w-[280px]">
                  {roleLoading ? (<div className="flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin"/>{t("updating")}</div>) : (<SelectValue />)}
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (<SelectItem key={r.value} value={r.value}>
                      <div>
                        <span className="font-medium">{t(r.labelKey)}</span>
                        <span className="text-xs text-muted-foreground ml-2">— {t(r.descriptionKey)}</span>
                      </div>
                    </SelectItem>))}
                </SelectContent>
              </Select>)}
            </div>
          </div>
        </div>
      </Card>

      {/* Actions */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">{t("actions")}</h3>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button className="w-full sm:w-auto" variant={member.user?.status === 'SUSPENDED' ? 'default' : 'outline'} onClick={handleStatusToggle} disabled={statusLoading}>
              {statusLoading ? (<Loader2 size={16} className="mr-2 animate-spin"/>) : member.user?.status === 'SUSPENDED' ? (<UserCheck size={16} className="mr-2"/>) : (<UserX size={16} className="mr-2"/>)}
              {member.user?.status === 'SUSPENDED' ? t("activate_user") : t("suspend_user")}
            </Button>
            {!member.isOwner && (<Button className="w-full sm:w-auto" variant="destructive" onClick={() => setRemoveModal(true)}>
              <Trash2 size={16} className="mr-2"/>{t("remove_from_organization")}</Button>)}
          </div>
        </div>
      </Card>

      {/* Remove Confirmation Modal */}
      <Modal isOpen={removeModal} onClose={() => setRemoveModal(false)} title={t("remove_member")} size="sm">
        <div className="space-y-4">
          <p className="text-muted-foreground">{t("are_you_sure_you_want_to_remove")}<strong className="text-foreground">{member.user?.name}</strong>{t("from_this_organization_this_action_cannot_be_undone")}</p>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button className="w-full sm:w-auto" variant="outline" onClick={() => setRemoveModal(false)}>{t("cancel")}</Button>
            <Button className="w-full sm:w-auto" variant="destructive" onClick={handleRemove} disabled={removeLoading}>
              {removeLoading && <Loader2 size={16} className="mr-2 animate-spin"/>}{t("remove")}</Button>
          </div>
        </div>
      </Modal>
    </div>);
};
export default UserDetails;
